import { IGenEq, EGenMode, eqToSingle, IGenSingle, QSingle } from '@educraft/base';
import { IExercise, ExerciseData, drawQuestionResult, addResult } from '../exercise';
import { PdfUnit, PdfRect } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';

export class ExerciseEq implements IExercise {
  readonly name = 'ExerciseEq';
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = true;
  readonly injectAbc = false;

  private marginColumn = PdfUnit.FromMillimeters(3);
  private marginRow = PdfUnit.FromMillimeters(2);
  private answerLetterSize = PdfUnit.FromMillimeters(5);

  draw(data: ExerciseData): void {
    const {selEq} = data.item;
    if (selEq.length === 0) return;

    // Resolve selected generators and create a combined single generator
    const generators: IGenSingle[] = selEq.map((name) => {
      const eq = data.provider.getOne<IGenEq>(EGenMode.GenEq, name);
      return eqToSingle(eq);
    });

    const combinedGen: IGenSingle = {
      generate(genData) {
        const idx = genData.random.next(0, generators.length);
        return generators[idx].generate(genData);
      },
    };

    // Generate samples to measure sizes
    const samples: QSingle[] = [];
    const seen = new Set<string>();
    let sz1 = 0;
    let szY = 0;

    for (let i = 0; i < 20; i++) {
      const q = combinedGen.generate(data.data);
      const key = `${q.texts[0]}|${q.answer}`;
      if (!seen.has(key)) {
        seen.add(key);
        samples.push(q);
      }

      const size1 = data.page.measureString(q.texts[0], GUtil.exerciseFont);
      const size2 = q.answer.length * this.answerLetterSize;

      if (i === 0) {
        sz1 = size1.width + size2;
        szY = size1.height;
      } else {
        sz1 = Math.max(sz1, size1.width + size2);
        szY = Math.max(szY, size1.height);
      }
    }

    const countX = Math.max(1, Math.floor(data.rect.width / (sz1 + this.marginColumn)));
    const countY = Math.max(1, Math.floor(data.rect.height / (szY + this.marginRow)));
    const total = countX * countY;

    // Generate unique items to fill the grid
    const items = this.generateUnique(combinedGen, data, total);

    const cellW = (data.rect.width + this.marginColumn) / countX;
    const cellH = (data.rect.height + this.marginRow) / countY;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const index = y + x * countY;
        if (index >= items.length) break;
        const item = items[index];

        addResult(data, item.texts[0], item.answer);

        const r = new PdfRect(
          data.rect.x + x * cellW,
          data.rect.y + y * cellH,
          cellW - this.marginColumn,
          cellH - this.marginRow
        );
        drawQuestionResult(data, item.texts[0], r);
      }
    }
  }

  private generateUnique(gen: IGenSingle, data: ExerciseData, count: number): QSingle[] {
    const dict = new Map<string, QSingle>();
    let tries = 0;

    while (dict.size < count && tries < 10_000) {
      const q = gen.generate(data.data);
      const key = `${q.texts[0]}|${q.answer}`;
      if (!dict.has(key)) {
        dict.set(key, q);
      }
      tries++;
    }

    const items = Array.from(dict.values());

    // If we don't have enough unique items, fill with random picks
    while (items.length < count) {
      const q = gen.generate(data.data);
      items.push(q);
    }

    return items.slice(0, count);
  }
}
